import { useEffect, useState } from 'react';
import {
  adminVideoPreviewUrl,
  getVideoDelivery,
  publishVideoDelivery,
  reviewDeliveryVideo,
  revokeVideoDelivery,
  uploadDeliveryVideo,
  type VideoDelivery,
} from './api';

function megabytes(bytes: number): string {
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

export function VideoDeliveryPanel({
  companyId,
  csrfToken,
  narrationText,
  onError,
  projectId,
  revisionId,
}: {
  companyId: string;
  csrfToken: string;
  narrationText: string;
  onError: (error: unknown) => void;
  projectId: string;
  revisionId: string;
}) {
  const [delivery, setDelivery] = useState<VideoDelivery | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(0);
  const [notice, setNotice] = useState<string | null>(null);
  const [durationSeconds, setDurationSeconds] = useState(480);
  const [checks, setChecks] = useState({
    h264AacConfirmed: false,
    identityOverlayConfirmed: false,
    openingNarrationConfirmed: false,
    playbackConfirmed: false,
  });
  const [reviewNotes, setReviewNotes] = useState('');

  async function refresh(): Promise<void> {
    setDelivery(await getVideoDelivery(companyId, projectId, revisionId));
  }

  useEffect(() => {
    let active = true;
    void getVideoDelivery(companyId, projectId, revisionId)
      .then((value) => {
        if (active) setDelivery(value);
      })
      .catch(onError);
    return () => {
      active = false;
    };
  }, [companyId, projectId, revisionId]);

  async function upload(): Promise<void> {
    if (file === null) return;
    if (!file.name.toLocaleLowerCase('en-US').endsWith('.mp4')) {
      setNotice('MP4ファイルを選択してください。');
      return;
    }
    setBusy(true);
    setProgress(0);
    setNotice('動画を非公開の隔離領域へuploadしています。画面を閉じないでください。');
    try {
      const next = await uploadDeliveryVideo(
        companyId,
        projectId,
        revisionId,
        file,
        csrfToken,
        setProgress,
      );
      setDelivery(next);
      setFile(null);
      setNotice('サイズ・MP4署名・SHA-256を確認しました。次に実動画を再生して確認してください。');
    } catch (error) {
      onError(error);
      setNotice('動画uploadまたは自動検査に失敗しました。状態を確認して再実行してください。');
    } finally {
      setBusy(false);
    }
  }

  async function copyNarration(): Promise<void> {
    try {
      await navigator.clipboard.writeText(narrationText);
      setNotice('全スライドのナレーション原稿をコピーしました。');
    } catch (error) {
      onError(error);
    }
  }

  async function review(): Promise<void> {
    const upload = delivery?.upload;
    if (upload === null || upload === undefined) return;
    if (!Object.values(checks).every(Boolean)) {
      setNotice('4項目を実動画で確認してください。');
      return;
    }
    setBusy(true);
    try {
      setDelivery(
        await reviewDeliveryVideo(
          companyId,
          projectId,
          revisionId,
          upload.id,
          {
            durationSeconds,
            h264AacConfirmed: true,
            identityOverlayConfirmed: true,
            openingNarrationConfirmed: true,
            playbackConfirmed: true,
            reviewNotes: reviewNotes.trim() || null,
          },
          csrfToken,
        ),
      );
      setNotice('手動再生確認を記録しました。公開前の最終確認へ進めます。');
    } catch (error) {
      onError(error);
    } finally {
      setBusy(false);
    }
  }

  async function publish(): Promise<void> {
    if (!window.confirm('確認済みの動画と資料を契約企業の納品ページへ公開しますか？')) return;
    setBusy(true);
    try {
      setDelivery(await publishVideoDelivery(companyId, projectId, revisionId, csrfToken));
      setNotice('契約企業の専用納品ページへ公開しました。');
    } catch (error) {
      onError(error);
    } finally {
      setBusy(false);
    }
  }

  async function revoke(): Promise<void> {
    const reason = window.prompt('公開を取り消す理由を10文字以上で入力してください。');
    if (reason === null) return;
    if (reason.trim().length < 10) {
      setNotice('公開取消理由は10文字以上必要です。');
      return;
    }
    setBusy(true);
    try {
      setDelivery(
        await revokeVideoDelivery(companyId, projectId, revisionId, reason.trim(), csrfToken),
      );
      setNotice('企業向け公開を直ちに停止しました。ファイルは再確認用に非公開保存されています。');
    } catch (error) {
      onError(error);
    } finally {
      setBusy(false);
    }
  }

  const uploadRecord = delivery?.upload ?? null;
  const automatedReady =
    uploadRecord?.status === 'AUTOMATED_VERIFIED' || uploadRecord?.status === 'VERIFIED';
  const published = delivery?.publication?.status === 'PUBLISHED';

  return (
    <section className="video-delivery-panel">
      <div className="section-heading">
        <div>
          <p className="eyebrow">VIDEO &amp; CUSTOMER DELIVERY</p>
          <h4>動画作成・最終確認・公開</h4>
        </div>
        <button
          className="secondary"
          disabled={busy}
          onClick={() => void refresh().catch(onError)}
          type="button"
        >
          状態を更新
        </button>
      </div>

      {notice !== null && <div className="notice warning">{notice}</div>}
      <ol className="video-workflow">
        <li>生成済みGoogle SlidesをGoogle Vidsへ読み込みます。</li>
        <li>speaker notesをナレーションへ反映し、必要最低限の調整を行います。</li>
        <li>会社名・工事名・対象年月を常時表示し、冒頭でも読み上げます。</li>
        <li>H.264映像／AAC音声のMP4（8〜10分）を書き出してuploadします。</li>
      </ol>
      <button className="secondary" onClick={() => void copyNarration()} type="button">
        ナレーション原稿をコピー
      </button>
      <details className="narration-preview">
        <summary>ナレーション原稿を確認</summary>
        <pre>{narrationText}</pre>
      </details>

      {!published && uploadRecord?.status !== 'VERIFIED' && (
        <div className="video-upload-card">
          <label>
            完成MP4
            <input
              accept="video/mp4,.mp4"
              disabled={busy}
              onChange={(event) => setFile(event.target.files?.[0] ?? null)}
              type="file"
            />
          </label>
          {file !== null && (
            <p className="muted">
              {file.name} · {megabytes(file.size)}
            </p>
          )}
          {busy && progress > 0 && (
            <progress max={100} value={progress}>
              {progress}%
            </progress>
          )}
          <button disabled={busy || file === null} onClick={() => void upload()} type="button">
            {busy ? `upload中 ${progress}%` : 'MP4を検査用にupload'}
          </button>
        </div>
      )}

      {uploadRecord !== null && (
        <dl className="detail-list video-file-summary">
          <div>
            <dt>upload方式</dt>
            <dd>{uploadRecord.uploadMode === 'SINGLE' ? '単一' : '分割'}</dd>
          </div>
          <div>
            <dt>状態</dt>
            <dd>{uploadRecord.status}</dd>
          </div>
          <div>
            <dt>サイズ</dt>
            <dd>{megabytes(uploadRecord.sizeBytes ?? uploadRecord.expectedSizeBytes)}</dd>
          </div>
          <div>
            <dt>MP4署名</dt>
            <dd>{uploadRecord.signatureVerified ? '確認済み' : '未確認'}</dd>
          </div>
          <div>
            <dt>SHA-256</dt>
            <dd className="hash-value">{uploadRecord.sha256 ?? '未計算'}</dd>
          </div>
        </dl>
      )}

      {automatedReady && uploadRecord !== null && (
        <section className="video-review-card">
          <h5>公開前の実再生確認</h5>
          <video
            controls
            controlsList="nodownload"
            preload="metadata"
            src={adminVideoPreviewUrl(companyId, projectId, revisionId, uploadRecord.id)}
          >
            このブラウザは動画再生に対応していません。
          </video>
          {uploadRecord.status === 'AUTOMATED_VERIFIED' ? (
            <>
              <label>
                実測時間（秒・480〜600）
                <input
                  max={600}
                  min={480}
                  onChange={(event) => setDurationSeconds(Number(event.target.value))}
                  type="number"
                  value={durationSeconds}
                />
              </label>
              {(
                [
                  ['playbackConfirmed', '再生・一時停止・シーク・音量・全画面を確認'],
                  ['h264AacConfirmed', '書き出し設定がH.264映像／AAC音声'],
                  ['identityOverlayConfirmed', '会社名・工事名・対象年月が常時表示'],
                  [
                    'openingNarrationConfirmed',
                    '冒頭ナレーションで会社名・工事名・対象年月を読み上げ',
                  ],
                ] as const
              ).map(([key, label]) => (
                <label className="checkbox-line" key={key}>
                  <input
                    checked={checks[key]}
                    onChange={(event) =>
                      setChecks((current) => ({ ...current, [key]: event.target.checked }))
                    }
                    type="checkbox"
                  />
                  {label}
                </label>
              ))}
              <label>
                確認メモ（任意）
                <textarea
                  maxLength={2000}
                  onChange={(event) => setReviewNotes(event.target.value)}
                  value={reviewNotes}
                />
              </label>
              <button
                disabled={busy || !Object.values(checks).every(Boolean)}
                onClick={() => void review()}
                type="button"
              >
                手動再生確認を記録
              </button>
            </>
          ) : (
            <div className="notice success">
              手動再生確認済み（{uploadRecord.durationSeconds}秒）
            </div>
          )}
        </section>
      )}

      {delivery?.publication?.status === 'FINAL_REVIEW' && (
        <div className="final-publish-card">
          <h5>最終確認</h5>
          <p>
            画面用PDF、紙納品PDF、確認済みMP4を対象企業だけに公開します。公開は自動では行われません。
          </p>
          <button disabled={busy} onClick={() => void publish()} type="button">
            企業へ公開
          </button>
        </div>
      )}
      {published && (
        <div className="notice success publication-state">
          <strong>公開中</strong>
          <span>契約企業の専用納品ページからのみ閲覧できます。</span>
          <button
            className="danger ghost"
            disabled={busy}
            onClick={() => void revoke()}
            type="button"
          >
            公開を取り消す
          </button>
        </div>
      )}
      {delivery?.publication?.status === 'REVOKED' && (
        <div className="notice warning">
          公開取消済みです。再公開には最終確認をやり直してください。
        </div>
      )}
    </section>
  );
}
